import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { eventService } from '../../../services/eventService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const EventsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'completed', 'archived'

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await eventService.getEvents(user.id);
        setEvents(data);
      } catch (err) {
        console.error('Failed to load events', err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.id) fetchEvents();
  }, [user]);

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  const displayEvents = events.filter(e => {
    if (activeTab === 'active') return ['upcoming', 'active'].includes(e.status);
    if (activeTab === 'completed') return e.status === 'completed';
    return e.status === 'archived';
  });

  return (
    <div className="max-w-6xl pb-12">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Events</h1>
          <p className="mt-2 text-slate-600">Contextualize your networking by associating cards and connections with real-world events.</p>
        </div>
        <Button onClick={() => navigate('/app/events/new')}>
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" /> Create Event
        </Button>
      </header>

      <div className="flex space-x-1 bg-slate-200/50 p-1 rounded-xl w-fit mb-6">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'active' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Active / Upcoming
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'completed' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Completed
        </button>
        <button
          onClick={() => setActiveTab('archived')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'archived' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Archived
        </button>
      </div>

      {displayEvents.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiIcons.FiCalendar} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">No events found</h3>
          <p className="text-slate-500 max-w-sm mx-auto mb-6">
            Create an Event to measure networking activity from a conference, workshop, or professional gathering.
          </p>
          {activeTab === 'active' && <Button onClick={() => navigate('/app/events/new')}>Create Event</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayEvents.map(event => (
            <div key={event.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow">
              <div className="p-6 flex-1">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-bold text-slate-900 truncate pr-4">{event.name}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    event.status === 'active' ? 'bg-green-100 text-green-800' :
                    event.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                    event.status === 'completed' ? 'bg-slate-100 text-slate-800' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {event.status}
                  </span>
                </div>
                <div className="space-y-2 text-sm text-slate-600">
                  <p className="flex items-center"><SafeIcon icon={FiIcons.FiCalendar} className="mr-2 text-slate-400" /> {new Date(event.start_date).toLocaleDateString()}</p>
                  {event.location && <p className="flex items-center"><SafeIcon icon={FiIcons.FiMapPin} className="mr-2 text-slate-400" /> <span className="truncate">{event.location}</span></p>}
                </div>
                <div className="mt-6 flex items-center justify-between text-sm border-t border-slate-100 pt-4">
                  <div className="text-center">
                    <p className="font-bold text-slate-900">{event.event_cards?.[0]?.count || 0}</p>
                    <p className="text-xs text-slate-500">Cards</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-slate-900">{event.card_leads?.[0]?.count || 0}</p>
                    <p className="text-xs text-slate-500">Connections</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 px-4 py-3 border-t border-slate-100 flex gap-2">
                <Button variant="secondary" className="flex-1" size="sm" onClick={() => navigate(`/app/events/${event.id}`)}>
                  Manage Event
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'completed' && displayEvents.length > 0 && (
        <div className="mt-12 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="text-base font-bold text-slate-900 mb-4 border-b border-slate-100 pb-2">Event Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-slate-500 border-b border-slate-100">
                  <th className="pb-3 font-medium">Event Name</th>
                  <th className="pb-3 font-medium text-right">Date</th>
                  <th className="pb-3 font-medium text-right">Connections</th>
                </tr>
              </thead>
              <tbody>
                {displayEvents.map(e => (
                  <tr key={e.id} className="border-b border-slate-50">
                    <td className="py-3 font-medium text-slate-900">{e.name}</td>
                    <td className="py-3 text-right text-slate-600">{new Date(e.start_date).toLocaleDateString()}</td>
                    <td className="py-3 text-right text-slate-900 font-bold">{e.card_leads?.[0]?.count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};